import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, ArrowRight, ArrowLeft, Target, Mail, CheckCircle2, Plus, X, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { trackOnboardingCompleted } from '../lib/analytics';
import { hapticSuccess } from '../lib/nativeFeel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Guided prompts for the future self letter
const LETTER_PROMPTS = [
  "How does it feel to have achieved this?",
  "What are you most proud of?",
  "What does your daily life feel like now?",
  "Who have you become?",
  "What habits or mindset got you here?",
  "What are you grateful for?"
];

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    target_date: '',
    // Future Self Letter fields (replacing "why")
    future_self_letter_body: '',
    future_self_letter_date: '',
    identity_statement: '',
    daily_actions: ['', '', ''],
  });

  const totalSteps = 4;

  const handleNext = () => {
    if (step === 1 && (!formData.title || !formData.description)) {
      toast.error('Please fill in all fields');
      return;
    }
    if (step === 2 && (!formData.target_date || !formData.future_self_letter_body.trim() || !formData.future_self_letter_date)) {
      if (!formData.target_date) {
        toast.error('Please set your target date');
      } else if (!formData.future_self_letter_date) {
        toast.error('Please add a date to your letter');
      } else {
        toast.error('Please write your letter from your future self');
      }
      return;
    }
    if (step === 3 && !formData.identity_statement) {
      toast.error('Please define your future identity');
      return;
    }
    if (step === 4) {
      const validActions = formData.daily_actions.filter(a => a.trim());
      if (validActions.length < 3) {
        toast.error('Please add at least 3 daily actions');
        return;
      }
    }
    
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    const validActions = formData.daily_actions.filter(a => a.trim());
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          target_date: formData.target_date,
          identity_statement: formData.identity_statement,
          daily_actions: validActions,
          future_self_letter_body: formData.future_self_letter_body,
          future_self_letter_date: formData.future_self_letter_date,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to complete onboarding');
      }

      toast.success('Your journey begins now!');
      hapticSuccess();
      trackOnboardingCompleted({ actions_count: validActions.length });
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addAction = () => {
    if (formData.daily_actions.length < 5) {
      setFormData({
        ...formData,
        daily_actions: [...formData.daily_actions, ''],
      });
    }
  };

  const removeAction = (index) => {
    if (formData.daily_actions.length > 3) {
      const newActions = formData.daily_actions.filter((_, i) => i !== index);
      setFormData({ ...formData, daily_actions: newActions });
    }
  };

  const updateAction = (index, value) => {
    const newActions = [...formData.daily_actions];
    newActions[index] = value;
    setFormData({ ...formData, daily_actions: newActions });
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-8 animate-fade-in">
            <div className="text-center mb-10">
              <div className="w-16 h-16 rounded-full bg-[#8BAA9E]/20 flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-[#8BAA9E]" strokeWidth={1.5} />
              </div>
              <h2 className="font-serif text-3xl text-[#DCD3CA] mb-3">Define Your Goal</h2>
              <p className="text-[#B5B1AB] font-sans font-light">What transformation are you pursuing?</p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="text-[#B5B1AB] text-sm font-sans mb-2 block">Goal Title</label>
                <Input
                  placeholder="e.g., Become a published author"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full bg-[#252528] border-white/10 rounded-2xl py-4 px-5 text-[#DCD3CA] placeholder-[#7A7670] focus:border-[#8BAA9E]"
                  data-testid="goal-title-input"
                />
              </div>

              <div>
                <label className="text-[#B5B1AB] text-sm font-sans mb-2 block">Description</label>
                <Textarea
                  placeholder="Describe your goal in detail..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-[#252528] border-white/10 rounded-2xl py-4 px-5 text-[#DCD3CA] placeholder-[#7A7670] focus:border-[#8BAA9E] min-h-[120px] resize-none"
                  data-testid="goal-description-input"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#C6A75E]/20 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-[#C6A75E]" strokeWidth={1.5} />
              </div>
              <h2 className="font-serif text-3xl text-[#DCD3CA] mb-3">Letter From Your Future Self</h2>
              <p className="text-[#B5B1AB] font-sans font-light max-w-md mx-auto">
                Write a letter from your future self, sharing how you achieved this goal and how it feels to now live as the version of you who once dreamed of this life.
              </p>
            </div>

            {/* Target Date */}
            <div>
              <label className="text-[#B5B1AB] text-sm font-sans mb-2 block">Target Date for Your Goal</label>
              <Input
                type="date"
                value={formData.target_date}
                onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                className="w-full bg-[#252528] border-white/10 rounded-2xl py-4 px-5 text-[#DCD3CA] focus:border-[#8BAA9E]"
                data-testid="target-date-input"
              />
            </div>

            {/* Letter Date */}
            <div>
              <label className="text-[#B5B1AB] text-sm font-sans mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Date of Your Letter (from the future)
              </label>
              <Input
                type="date"
                value={formData.future_self_letter_date}
                onChange={(e) => setFormData({ ...formData, future_self_letter_date: e.target.value })}
                className="w-full bg-[#252528] border-white/10 rounded-2xl py-4 px-5 text-[#DCD3CA] focus:border-[#C6A75E]"
                data-testid="letter-date-input"
              />
            </div>

            {/* Guided Prompts */}
            <div className="bg-[#252528]/50 border border-white/5 rounded-2xl p-4">
              <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-3 font-sans">Consider these as you write:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {LETTER_PROMPTS.map((prompt) => (
                  <div key={prompt} className="flex items-start gap-2 text-[#B5B1AB] text-sm font-sans">
                    <span className="text-[#8BAA9E] mt-0.5">•</span>
                    <span>{prompt}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Letter Body */}
            <div>
              <label className="text-[#B5B1AB] text-sm font-sans mb-2 block">Your Letter</label>
              <Textarea
                placeholder="Dear present me,

I'm writing to you from the future, and I want you to know that everything you're working toward is worth it...

[Continue writing from your future self's perspective]"
                value={formData.future_self_letter_body}
                onChange={(e) => setFormData({ ...formData, future_self_letter_body: e.target.value })}
                className="w-full bg-[#252528] border-white/10 rounded-2xl py-5 px-5 text-[#DCD3CA] placeholder-[#7A7670] focus:border-[#C6A75E] min-h-[220px] resize-none font-sans leading-relaxed"
                data-testid="future-letter-input"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8 animate-fade-in">
            <div className="text-center mb-10">
              <div className="w-16 h-16 rounded-full bg-[#8BAA9E]/20 flex items-center justify-center mx-auto mb-6">
                <Leaf className="w-8 h-8 text-[#8BAA9E]" strokeWidth={1.5} />
              </div>
              <h2 className="font-serif text-3xl text-[#DCD3CA] mb-3">Future Identity</h2>
              <p className="text-[#B5B1AB] font-sans font-light">Who are you becoming?</p>
            </div>

            <div className="bg-[#252528] border border-white/5 rounded-[24px] p-6 mb-6">
              <p className="text-[#B5B1AB] text-sm font-sans mb-4">
                Write a present-tense statement about your future self. Speak as if you've already achieved your goal.
              </p>
              <div className="space-y-3 text-sm text-[#7A7670]">
                <p>• "I am a disciplined writer who creates every single day."</p>
                <p>• "I am someone who prioritizes my health and energy."</p>
                <p>• "I am a confident speaker who inspires others."</p>
              </div>
            </div>

            <div>
              <label className="text-[#B5B1AB] text-sm font-sans mb-2 block">Your Identity Statement</label>
              <Textarea
                placeholder="I am..."
                value={formData.identity_statement}
                onChange={(e) => setFormData({ ...formData, identity_statement: e.target.value })}
                className="w-full bg-[#252528] border-white/10 rounded-2xl py-4 px-5 text-[#DCD3CA] placeholder-[#7A7670] focus:border-[#8BAA9E] min-h-[120px] resize-none font-serif text-lg"
                data-testid="identity-statement-input"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8 animate-fade-in">
            <div className="text-center mb-10">
              <div className="w-16 h-16 rounded-full bg-[#8BAA9E]/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-[#8BAA9E]" strokeWidth={1.5} />
              </div>
              <h2 className="font-serif text-3xl text-[#DCD3CA] mb-3">Daily Actions</h2>
              <p className="text-[#B5B1AB] font-sans font-light">What will you do each day to become this person?</p>
            </div>

            <div className="bg-[#252528] border border-white/5 rounded-[24px] p-6 mb-6">
              <p className="text-[#B5B1AB] text-sm font-sans">
                Define 3-5 small, daily actions that align with your future identity. These should be specific and achievable.
              </p>
            </div>

            <div className="space-y-4">
              {formData.daily_actions.map((action, index) => (
                <div key={`action-${index}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#8BAA9E]/20 flex items-center justify-center text-[#8BAA9E] text-sm font-sans">
                    {index + 1}
                  </div>
                  <Input
                    placeholder={`Daily action ${index + 1}...`}
                    value={action}
                    onChange={(e) => updateAction(index, e.target.value)}
                    className="flex-1 bg-[#252528] border-white/10 rounded-2xl py-4 px-5 text-[#DCD3CA] placeholder-[#7A7670] focus:border-[#8BAA9E]"
                    data-testid={`daily-action-${index}`}
                  />
                  {formData.daily_actions.length > 3 && (
                    <button
                      onClick={() => removeAction(index)}
                      className="p-2 text-[#CF6679] hover:bg-[#CF6679]/20 rounded-full"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}

              {formData.daily_actions.length < 5 && (
                <button
                  onClick={addAction}
                  className="w-full py-4 border border-dashed border-[#666666] rounded-2xl text-[#B5B1AB] hover:border-[#8BAA9E] hover:text-[#8BAA9E] transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add another action
                </button>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#1C1C1E] py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-12">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`h-2 rounded-full transition-all ${
                s === step ? 'w-8 bg-[#8BAA9E]' : s < step ? 'w-2 bg-[#8BAA9E]' : 'w-2 bg-[#333333]'
              }`}
            />
          ))}
        </div>

        {/* Step content */}
        {renderStep()}

        {/* Navigation */}
        <div className="mt-12 flex gap-4">
          {step > 1 && (
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1 py-6 rounded-2xl border-white/10 text-[#B5B1AB] hover:bg-white/5"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          )}
          <Button
            onClick={handleNext}
            disabled={loading}
            className={`flex-1 py-6 rounded-2xl bg-[#8BAA9E] hover:bg-[#8BAA9E]/90 text-[#1C1C1E] font-sans font-medium ${
              step === 1 ? 'w-full' : ''
            }`}
            data-testid="onboarding-next-btn"
          >
            {loading ? (
              'Creating...'
            ) : step === totalSteps ? (
              'Begin Journey'
            ) : (
              <>
                Continue
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
